import React, { useRef } from "react";
import { motion } from "framer-motion";
import { appleLogin, facebookLogin, googleLogin, useAppDispatch } from "../../../redux";
import { useGoogleLogin } from "@react-oauth/google";
import { appleAuthHelpers } from "react-apple-signin-auth";
import AppleSignin from "react-apple-signin-auth";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props"; // custom render
import { ApplePayload, RoleEnum } from "../../shared/model";
import { useLocation } from "react-router-dom";
const SocialLogins = (props?: any) => {

    const location = useLocation();
    const dispatch = useAppDispatch();
    const facebookLoginProcessing = useRef(false);
    const googleLoginApi = useGoogleLogin({
        onSuccess: tokenResponse => {
            try {
                dispatch(googleLogin({ idToken: tokenResponse.access_token, userRole: props.role }));
            } catch (error) {
                alert("Something went wrong");
            }
    

        },
    });

    const handleAppleLogin = async (response) => {
        try {
            if (response && response.authorization) {
                const body = {} as ApplePayload;
                body.idToken = response.authorization.id_token;
                body.userRole = props.role;
                if (response.user && response.user.name) {
                    body.firstName = response.user.name.firstName;
                    body.lastName = response.user.name.lastName;
                }
                dispatch(appleLogin(body));
            }
        } catch (error) {
            alert("Something went wrong");
        }
    };
    // Facebook login callback
    const handleFacebookCallback = (response: any) => {
        // Prevent duplicate calls
        if (facebookLoginProcessing.current) {
            console.log("Facebook login already processing, ignoring duplicate callback");
            return;
        }

        try {
            console.log("Facebook callback response:", response);

            // Check if response is an error object (Facebook SDK error format)
            // Facebook SDK can return error in different formats
            if (response?.error) {
                console.error("Facebook login error:", response.error);
                facebookLoginProcessing.current = false;
                return;
            }

            if (response?.errorMessage) {
                console.error("Facebook login error message:", response.errorMessage);
                facebookLoginProcessing.current = false;
                return;
            }

            // Handle different response statuses
            // Note: status can be "connected", "not_authorized", "unknown", etc.
            if (response?.status === "unknown" || response?.status === "not_authorized") {
                console.error("Facebook login cancelled or not authorized:", response.status);
                facebookLoginProcessing.current = false;
                return;
            }

            // Check if we have a valid access token
            // This is the most important check - without accessToken, we can't proceed
            if (!response?.accessToken) {
                console.error("Facebook login failed: No access token received", response);
                facebookLoginProcessing.current = false;
                return;
            }

            // If we have an accessToken, proceed even if status is not explicitly "connected"
            // Some Facebook SDK versions don't always set status correctly
            if (response?.accessToken) {
                // Mark as processing
                facebookLoginProcessing.current = true;

                // Dispatch the login action with the access token
                dispatch(facebookLogin({ accessToken: response.accessToken, userRole: props.role }))
                    .then(() => {
                        // Success - navigation will happen in the action
                        console.log("Facebook login successful, navigation will occur");
                    })
                    .catch((error) => {
                        console.error("Facebook login dispatch error:", error);
                    })
                    .finally(() => {
                        // Reset processing flag after a delay to allow for navigation
                        setTimeout(() => {
                            facebookLoginProcessing.current = false;
                        }, 2000);
                    });
            }
        } catch (error) {
            console.error("Facebook login error:", error);
            facebookLoginProcessing.current = false;
            // Don't show alert here, let the Redux action handle error display
        }
    };

    // Facebook login error handler
    const handleFacebookError = (error: any) => {
        console.error("Facebook login error handler:", error);
        // Reset processing flag on error
        facebookLoginProcessing.current = false;
        
        // Handle specific Facebook errors
        if (error?.error) {
            const errorCode = error.error.code || error.error.error_code;
            const errorMessage = error.error.message || error.errorMessage;
            
            console.error("Facebook error details:", {
                code: errorCode,
                message: errorMessage,
                type: error.error.type,
                error_subcode: error.error.error_subcode
            });
            
            // Common Facebook error codes:
            // 200 - Permissions error
            // 190 - Invalid OAuth 2.0 Access Token
            // 102 - Session key invalid
            // 10 - Permission denied
        }
        
        // The error handler is called when Facebook SDK encounters an error
        // The callback might still be called with an error response, so we handle both
    };
    return (
        <>
            <div className="grid grid-cols-2 gap-4">
                <motion.button
                    disabled={location.pathname === "/auth/register" && !props.role}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => googleLoginApi()}
                    whileTap={{ scale: 0.98 }}
                    className="flex items-center justify-center gap-2 p-2.5 border rounded-lg hover:bg-gray-50 transition-colors">
                    <img
                        src="https://img.icons8.com/?size=100&id=17949&format=png&color=000000"
                        alt="Google"
                        className="w-5 h-5"
                    />
                    <span className="text-sm font-medium">Google</span>
                </motion.button>
                <AppleSignin
                    render={(renderProps: any) => (
                        <motion.button
                            disabled={location.pathname === "/auth/register" && !props.role}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={renderProps.onClick}
                            className="flex items-center justify-center gap-2 p-2.5 border rounded-lg hover:bg-gray-50 transition-colors">
                            <img
                                src="https://img.icons8.com/?size=100&id=30840&format=png&color=000000"
                                alt="Facebook"
                                className="w-5 h-5"
                            />
                            <span className="text-sm font-medium">Apple</span>
                        </motion.button>
                    )}
                    /** Auth options passed to AppleID.auth.init() */
                    authOptions={{
                        /** Client ID - eg: 'com.example.com' */
                        clientId: process.env.REACT_APP_APPLE_CLIENT_ID,
                        usePopup: true,
                        /** Requested scopes, seperated by spaces - eg: 'email name' */
                        scope: "email name",
                        /** Apple's redirectURI - must be one of the URIs you added to the serviceID - the undocumented trick in apple docs is that you should call auth from a page that is listed as a redirectURI, localhost fails */
                        redirectURI: process.env.REACT_APP_APPLE_REDIRECT_URI,
                        /** State string that is returned with the apple response */
                        state: "state",
                        /** Nonce */
                        nonce: "nonce",
                        /** Uses popup auth instead of redirection */
                    }} // REQUIRED

                    uiType="light"
                    className="flex items-center justify-center gap-2 p-2.5 border rounded-lg hover:bg-gray-50 transition-colors apple-auth"
                    buttonExtraChildren="Apple"
                    onSuccess={response => handleAppleLogin(response)} // default = undefined
                    /** Called upon signin error */
                    onError={error => {}} // default = undefined
                />
            </div>

            {/* Facebook Button using custom render */}
            <div className="grid grid-cols-2 gap-4">
                <FacebookLogin
                    appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                    autoLoad={false}
                    fields="name,email,picture"
                    scope="public_profile,email"
                    callback={handleFacebookCallback}
                    onFailure={handleFacebookError}
                    isMobile={false}
                    disableMobileRedirect={true}
                    redirect={false}
                    version="12.0"
                    cookie={false}
                    xfbml={false}
                    render={(renderProps: any) => (
                        <motion.button
                            disabled={location.pathname === "/auth/register" && !props.role}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={renderProps.onClick}
                            className="flex items-center justify-center gap-2 p-2.5 border rounded-lg hover:bg-gray-50 transition-colors">
                            <img
                                src="https://img.icons8.com/?size=100&id=118497&format=png&color=000000"
                                alt="Facebook"
                                className="w-5 h-5"
                            />
                            <span className="text-sm font-medium">Facebook</span>
                        </motion.button>
                    )}
                />
            </div>


            {/* <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleAppleLogin()}
                className="w-[48%] flex items-center justify-center gap-2 p-2.5 border rounded-lg hover:bg-gray-50 transition-colors">
                <img
                    src="https://img.icons8.com/?size=100&id=30840&format=png&color=000000"
                    alt="Apple"
                    className="w-5 h-5"
                />
                <span className="text-sm font-medium">Apple</span>
            </motion.button> */}
        </>
    );
};

export default SocialLogins;